// src/components/common/Select.tsx
import type { SelectOption } from '@/types';
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface SelectProps {
    value: string
    onChange: (event: { target: { name: string; value: string } }) => void
    options: SelectOption[]
    placeholder?: string
    emptyMessage?: string
    className?: string
    disabled?: boolean
    name?: string
}

export interface DropdownPosition {
    top: number
    left: number
    width: number
    openUp: boolean
}

const Select = ({
    value,
    onChange,
    options,
    placeholder = "Seleziona...",
    emptyMessage = "Nessuna opzione disponibile",
    className = "",
    disabled = false,
    name = ""
}: SelectProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({ top: 0, left: 0, width: 0, openUp: false });
    const dropdownRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);

    // Calculate dropdown position, opening upward if not enough space below
    const updateDropdownPosition = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const maxDropdownHeight = 256; // matches max-h-64 (16rem = 256px)
            const shouldOpenUp = spaceBelow < maxDropdownHeight && rect.top > spaceBelow;

            setDropdownPosition({
                top: shouldOpenUp
                    ? rect.top + window.scrollY
                    : rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width,
                openUp: shouldOpenUp
            });
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    // Update position when dropdown opens or window resizes/scrolls
    useEffect(() => {
        if (isOpen) {
            updateDropdownPosition();
            window.addEventListener('scroll', updateDropdownPosition, true);
            window.addEventListener('resize', updateDropdownPosition);

            return () => {
                window.removeEventListener('scroll', updateDropdownPosition, true);
                window.removeEventListener('resize', updateDropdownPosition);
            };
        }
    }, [isOpen]);

    const handleSelect = (selectedValue: string) => {
        onChange({ target: { name: name, value: selectedValue } });
        setIsOpen(false);
    };

    const getDisplayValue = (): string => {
        if (!value) return placeholder;
        const selected = options.find(opt => opt.value === value);
        return selected?.label || value;
    };

    const handleTriggerClick = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    // Dropdown content
    const dropdownContent = isOpen ? (
        <div
            ref={dropdownRef}
            className="fixed bg-white border border-slate-300 rounded-lg shadow-lg max-h-64 overflow-y-auto"
            style={{
                ...(dropdownPosition.openUp
                    ? { bottom: `${window.innerHeight - dropdownPosition.top}px` }
                    : { top: `${dropdownPosition.top}px` }),
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                zIndex: 9999
            }}
        >
            {options.length > 0 ? (
                options.map((option) => (
                    <div
                        key={option.value}
                        className={`px-4 py-3 cursor-pointer hover:bg-blue-50 transition-colors ${value === option.value ? 'bg-blue-100 font-medium text-blue-700' : 'text-slate-700'
                            }`}
                        onClick={() => handleSelect(option.value)}
                    >
                        {option.label}
                    </div>
                ))
            ) : (
                <div className="px-4 py-3 text-slate-500 text-center">
                    {emptyMessage}
                </div>
            )}
        </div>
    ) : null;

    return (
        <>
            <div className={className}>
                <div
                    ref={triggerRef}
                    className={`w-full px-4 py-3 border rounded-lg flex items-center justify-between cursor-pointer transition-colors ${disabled
                        ? 'bg-slate-100 border-slate-300 cursor-not-allowed'
                        : isOpen
                            ? 'border-blue-500 ring-2 ring-blue-500'
                            : 'border-slate-300 hover:border-slate-400'
                        }`}
                    onClick={handleTriggerClick}
                >
                    <span className={value ? 'text-slate-800' : 'text-slate-400'}>
                        {getDisplayValue()}
                    </span>
                    <ChevronDown
                        className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                </div>
            </div>
            {dropdownContent && createPortal(dropdownContent, document.body)}
        </>
    );
};

export default Select;
